import { useMemo, useRef } from 'react'
import { showNotification } from '@mantine/notifications'
import debounce from 'lodash/debounce'
import { v4 as uuid } from 'uuid'

import { useConfirmSubmitModal } from './useConfirmSubmitModal.tsx'
import { useFeedbackFormTracking } from './useFeedbackFormTracking'

import {
  DEFAULT_SA_FIELDS,
  DEFAULT_WITNESS_CR_FIELDS,
} from '@/components/Event/CrForm/saFields'
import { getStateRecordScript } from '@/components/Event/CrForm/stateRecordScripts'
import {
  announceRecordValidation,
  buildEventFeedbackPayload,
  cnaDeclaredAtValidation,
  cnaTypeOtherTextValidation,
  cnaTypeValidation,
  nameValidation,
  ordersCompleteValidation,
  ordersPresentValidation,
  presentValidation,
  reporterCountyValidation,
  reporterStateValidation,
  roleValidation,
  transcriptOrderedValidation,
  transcriptOrdersValidation,
  turnaroundHoursValidation,
  uniqueEmailValidation,
  witnessLocationValidation,
} from '@/components/Event/Feedback/validation'
import { useEventZoomParticipants } from '@/components/Event/NewScript/queries/scriptData'
import { AttendeeRole, TurnaroundHours } from '@/constants'
import { useTrackUserAction } from '@/hooks/analytics/useAnalytics'
import { useForm } from '@/hooks/useForm'
import {
  ReporterFeedbackPayload,
  useEndEventMutation,
  useSaveReporterFeedbackMutation,
  useSubmitReporterFeedbackMutation,
} from '@/queries/events'
import {
  Event,
  EventFeedback,
  Reporter,
  ReporterFeedback,
  ZoomParticipant,
} from '@/types'
import {
  isWithClientReporter,
  isWithSessionAssistant,
  isWitnessLocationRequired,
  shouldProvideTranscriptOrders,
  shouldSaTakeTranscriptOrders,
} from '@/utils/parrot'

const AUTOSAVE_DEBOUNCE_MS = 2000
// Longest an edit can sit unsaved while the SA keeps typing.
const AUTOSAVE_MAX_WAIT_MS = 10_000

export const useFeedbackForm = ({
  event,
  reporter,
  events_feedbacks,
  steps,
  sa,
  staged = false,
  onSubmit,
}: {
  event: Event
  reporter: Reporter | undefined
  events_feedbacks: EventFeedback[] | undefined
  steps?: ReporterFeedback['steps']
  sa?: ReporterFeedback['sa']
  // Staged CR Form: enables CR-Form-only validation like Zoom mapping.
  staged?: boolean
  onSubmit: () => void
}) => {
  const trackUserAction = useTrackUserAction()
  const { trackOnSave } = useFeedbackFormTracking(event.id)
  const confirmAsync = useConfirmSubmitModal(event)
  const { mutateAsync: endAsync } = useEndEventMutation(event, {
    onErrorMessage: message => {
      showNotification({ message, color: 'red' })
    },
  })
  const { mutateAsync: submitAsync } = useSubmitReporterFeedbackMutation(
    event,
    {
      onErrorMessage: message => {
        showNotification({ message, color: 'red' })
      },
    },
  )
  const { mutateAsync: saveAsync, isLoading: isSaving } =
    useSaveReporterFeedbackMutation(event, {
      onErrorMessage: message => {
        showNotification({ message, color: 'red' })
      },
    })

  const debouncedSaveAsync = useMemo(
    () =>
      debounce(
        (values: ReporterFeedback) => {
          saveAsync(values as ReporterFeedbackPayload)
          trackOnSave(values)
        },
        AUTOSAVE_DEBOUNCE_MS,
        { maxWait: AUTOSAVE_MAX_WAIT_MS },
      ),
    [saveAsync, trackOnSave],
  )

  // Mirror the DR script: the Zoom-name mapping is only shown/required when the event
  // actually has Zoom participants. In feature deployments (no real Zoom data) there are
  // none, so the mapping is skipped. Read via a ref so the validate closure sees the
  // current value at validation time regardless of when it was created.
  // Polled like the DR script so late joiners reach the dropdown without a reload, but
  // only while live — the list is frozen afterwards, though the query stays enabled so
  // the waiting/past pages still validate the mapping.
  const zoomParticipantsQuery = useEventZoomParticipants(event, {
    enabled: staged,
    refetchInterval: event.is_ongoing ? 10_000 : false,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })
  const zoomParticipantsRef = useRef<ZoomParticipant[]>([])
  zoomParticipantsRef.current = zoomParticipantsQuery.data ?? []

  const form = useForm<ReporterFeedback>({
    initialValues: {
      reporter_state: reporter?.state ?? '',
      reporter_county: reporter?.county ?? '',
      events_feedbacks: events_feedbacks?.length
        ? getEventFeedbacksInitialValuesFromSavedValues(
            events_feedbacks as ReporterFeedback['events_feedbacks'],
          )
        : getEventFeedbacksInitialValues(event, reporter),
      // Step UI state (CR Form). Carried through the existing autosave; absent for
      // non-staged events. `getReporterFeedbackPayload` spreads it via `...values`.
      steps,
      // Scripted CR Form fields, seeded with defaults so every sa.* path exists.
      sa: { ...DEFAULT_SA_FIELDS, ...(sa ?? {}) },
    },

    validate: {
      reporter_state: isWithClientReporter(event)
        ? () => null
        : reporterStateValidation,
      reporter_county: isWithClientReporter(event)
        ? () => null
        : reporterCountyValidation,
      ...(staged
        ? {
            sa: {
              cr_joined: value =>
                value === null ? 'Please select an option' : null,
              // CA and WA hide the announce question: the SA always calls the record.
              announce_record: getStateRecordScript(event)
                ? () => null
                : announceRecordValidation,
            },
            // Every section must be checked done before submitting.
            steps: stepsValue => {
              const allDone = Object.values(stepsValue ?? {}).every(
                step => step.done,
              )
              return allDone
                ? null
                : 'Mark all the steps as completed before submitting.'
            },
            events_feedbacks: {
              attendees: {
                name: nameValidation,
                role: roleValidation,
                email: (email: string | null, values: unknown, key: string) =>
                  uniqueEmailValidation(
                    email,
                    (values as ReporterFeedback).events_feedbacks[
                      parseInt(key.split('.')[1])
                    ],
                  ),
                present: presentValidation,
                city: witnessLocationValidation(
                  isWitnessLocationRequired(event),
                  'City',
                ),
                state: witnessLocationValidation(
                  isWitnessLocationRequired(event),
                  'State',
                ),
                zoom_participants: (
                  value: ZoomParticipant[] | undefined,
                  values: unknown,
                  key: string,
                ) => {
                  // No real Zoom participants (e.g. feature deployments) — the mapping
                  // field is hidden and not required, same as the DR script.
                  if (zoomParticipantsRef.current.length === 0) {
                    return null
                  }
                  const parts = key.split('.')
                  const attendee = (values as ReporterFeedback)
                    .events_feedbacks[parseInt(parts[1])]?.attendees?.[
                    parseInt(parts[3])
                  ]
                  if (
                    attendee?.present === true &&
                    !(value as ZoomParticipant[])?.length
                  ) {
                    return 'Map this attendee to their Zoom participant before submitting.'
                  }
                  return null
                },
              },
              // Mirror the DR script: every Zoom participant must be assigned to an
              // attendee. Pseudo per-witness field (no stored value) — Mantine still runs
              // this function once per witness, so we compute coverage from that witness's
              // attendees and the event's Zoom participants.
              zoom_coverage: (
                _value: unknown,
                values: ReporterFeedback,
                key: string,
              ) => {
                const participants = zoomParticipantsRef.current
                if (participants.length === 0) {
                  return null
                }
                const witnessIndex = parseInt(key.split('.')[1])
                const attendees =
                  values.events_feedbacks[witnessIndex]?.attendees ?? []
                const mappedIds = new Set(
                  attendees.flatMap(attendee =>
                    (attendee.zoom_participants ?? []).map(zp => zp.id),
                  ),
                )
                const unmapped = participants.filter(p => !mappedIds.has(p.id))
                if (unmapped.length === 0) {
                  return null
                }
                return `Assign every Zoom participant to an attendee. Unassigned: ${unmapped
                  .map(p => p.user_name)
                  .join(', ')}`
              },
              cna_declared_at: (canDeclaredAt, values, key) =>
                cnaDeclaredAtValidation(
                  canDeclaredAt,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                ),
              cna_type: (cnaType, values, key) =>
                cnaTypeValidation(
                  cnaType,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                ),
              cna_type_other_text: (cnaTypeText, values, key) =>
                cnaTypeOtherTextValidation(
                  cnaTypeText,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                ),
              // When orders are present, require at least one filled order and no pending
              // (Expedited-without-a-day) selections. Pseudo per-witness field, like above.
              orders_complete: (
                _value: unknown,
                values: ReporterFeedback,
                key: string,
              ) =>
                ordersCompleteValidation(
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                  shouldSaTakeTranscriptOrders(event),
                ),
              // Closing selects are required; without rules the step can be completed /
              // submitted with them null, silently falling back to payload defaults.
              read_waive: value => (value ? null : 'Please select an option'),
              orders_present: value =>
                ordersPresentValidation(
                  value,
                  shouldSaTakeTranscriptOrders(event),
                ),
              // Exactly one Witness per witness-event, mirroring the DR script. Pseudo
              // per-witness field so it validates the attendees list as a whole.
              witness_role: (
                _value: unknown,
                values: ReporterFeedback,
                key: string,
              ) => {
                const attendees =
                  values.events_feedbacks[parseInt(key.split('.')[1])]
                    ?.attendees ?? []
                const witnessCount = attendees.filter(
                  attendee => attendee.role === AttendeeRole.WITNESS,
                ).length
                return witnessCount === 1
                  ? null
                  : 'There must be exactly one witness.'
              },
            },
          }
        : {
            events_feedbacks: {
              // The input is hidden for some events (see shouldProvideTranscriptOrders),
              // so there's no way for the SA to satisfy this check if it fails - skip it.
              transcript_ordered: (transcriptOrdered, values, key) =>
                !shouldProvideTranscriptOrders(event)
                  ? null
                  : transcriptOrderedValidation(
                      transcriptOrdered,
                      values.events_feedbacks[parseInt(key.split('.')[1])],
                      {
                        orderRequired: event.is_transcript_order_required,
                      },
                      event.cna_transcript_orders_enabled,
                    ),
              turnaround_hours: (turnaroundHours, values, key) =>
                turnaroundHoursValidation(
                  turnaroundHours,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                  !!event.can_order_for_oc,
                ),
              transcript_orders: (turnaroundOrders, values, key) =>
                transcriptOrdersValidation(
                  turnaroundOrders,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                  {
                    canOrderForOc: !!event.can_order_for_oc,
                    isOrderRequired: event.is_transcript_order_required,
                  },
                ),
              attendees: {
                name: nameValidation,
                role: roleValidation,
                email: (email: string | null, values: unknown, key: string) =>
                  uniqueEmailValidation(
                    email,
                    (values as ReporterFeedback).events_feedbacks[
                      parseInt(key.split('.')[1])
                    ],
                  ),
                present: presentValidation,
              },
              cna_declared_at: (canDeclaredAt, values, key) =>
                cnaDeclaredAtValidation(
                  canDeclaredAt,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                ),
              cna_type: (cnaType, values, key) =>
                cnaTypeValidation(
                  cnaType,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                ),
              cna_type_other_text: (cnaTypeText, values, key) =>
                cnaTypeOtherTextValidation(
                  cnaTypeText,
                  values.events_feedbacks[parseInt(key.split('.')[1])],
                ),
            },
          }),
    },
    onChange: debouncedSaveAsync,
    validateInputOnChange: true,
    validateInputOnBlur: true,
  })

  const onSubmitWithErrors = (validationErrors: Record<string, string>) => {
    // intentionally log the error for easier debugging
    // eslint-disable-next-line no-console
    console.log(validationErrors)
    if (Object.values(validationErrors).length !== 0) {
      showNotification({
        message:
          'Form could not be submitted. Please fix the highlighted errors.',
        color: 'red',
      })

      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const getReporterFeedbackPayload = (
    values: ReporterFeedback,
  ): ReporterFeedbackPayload => {
    return {
      ...values,
      // Flatten the event-level CR Form note to the top level so Parrot receives it.
      // `|| undefined` omits it for DR/non-SA forms (empty sa), leaving them untouched.
      record_preferences: values.sa?.record_preferences || undefined,
      events_feedbacks: form.values.events_feedbacks.map(feedback =>
        buildEventFeedbackPayload(
          feedback,
          event.cna_transcript_orders_enabled,
          shouldSaTakeTranscriptOrders(event),
        ),
      ),
    } as ReporterFeedbackPayload
  }

  return {
    form,
    save: async () => {
      await saveAsync(getReporterFeedbackPayload(form.values))

      trackUserAction('Event form saved', { event_id: event.id })
      showNotification({ message: 'Form was saved' })
    },
    saving: isSaving,
    submit: async () => {
      await confirmAsync()

      if (event.is_ongoing) {
        await endAsync()
      }

      await submitAsync(getReporterFeedbackPayload(form.values))

      trackUserAction('Event form submitted', { event_id: event.id })
      onSubmit()
    },
    onSubmitWithErrors,
  }
}

const getEventFeedbacksInitialValues = (
  event: Event,
  reporter: Reporter | undefined,
): ReporterFeedback['events_feedbacks'] => {
  const witnesses = event.attendees.filter(
    ({ role }) => role === AttendeeRole.WITNESS,
  )
  const attendees = event.attendees.map(attendee => {
    return {
      id: uuid(),
      name: attendee.name,
      role: attendee.role,
      email: attendee.email,
      present: null,
    }
  })

  // add reporter as attendee if he is not listed in attendees list
  const reporterAttendees =
    !reporter ||
    event.attendees.map(({ email }) => email).includes(reporter.email)
      ? []
      : [
          {
            id: uuid(),
            name: reporter.name,
            role: isWithSessionAssistant(event)
              ? AttendeeRole.SESSION_ASSISTANT
              : AttendeeRole.DIGITAL_REPORTER,
            email: reporter.email,
            present: null,
          },
        ]

  if (witnesses.length === 0) {
    return [
      {
        result: 'completed',
        witness_requested_transcript: false,
        transcript_ordered: event.is_transcript_order_required,
        turnaround_hours: event.is_transcript_order_required
          ? TurnaroundHours.SEVEN_DAYS
          : TurnaroundHours.ROUGH_DRAFT,
        cna_type: null,
        cna_type_other_text: null,
        cna_declared_at: null,
        id_type: null,
        id_number: null,
        id_screenshots: [],
        timestamp_log: null,
        additional_notes: null,
        transcript_orders: null,
        ...DEFAULT_WITNESS_CR_FIELDS,
        attendees: [...reporterAttendees, ...attendees],
      },
    ]
  }

  return witnesses.map(witness => ({
    result: 'completed',
    witness_requested_transcript: false,
    transcript_ordered: event.is_transcript_order_required,
    turnaround_hours: event.is_transcript_order_required
      ? TurnaroundHours.SEVEN_DAYS
      : TurnaroundHours.ROUGH_DRAFT,
    cna_type: null,
    cna_type_other_text: null,
    cna_declared_at: null,
    id_type: null,
    id_number: null,
    id_screenshots: [],
    timestamp_log: null,
    additional_notes: null,
    transcript_orders: null,
    ...DEFAULT_WITNESS_CR_FIELDS,
    attendees: [
      ...reporterAttendees,
      ...attendees.map(attendee => ({
        ...attendee,
        role:
          attendee.role === AttendeeRole.WITNESS &&
          witness.name !== attendee.name
            ? AttendeeRole.OTHER
            : attendee.role,
      })),
    ],
  }))
}

const getEventFeedbacksInitialValuesFromSavedValues = (
  events_feedbacks: ReporterFeedback['events_feedbacks'],
): ReporterFeedback['events_feedbacks'] => {
  const attendeesMasterIdSet: Record<string, string> = {}
  events_feedbacks.forEach(({ attendees }) => {
    attendees.forEach(attendee => {
      if (attendee.name) {
        attendeesMasterIdSet[attendee.name] = uuid()
      }
    })
  })

  return events_feedbacks.map(({ attendees, ...feedback }) => ({
    ...DEFAULT_WITNESS_CR_FIELDS,
    ...feedback,
    attendees: attendees.map(attendee => ({
      ...attendee,
      id: attendee.name ? attendeesMasterIdSet[attendee.name] : uuid(),
    })),
  }))
}
